<div class="flex h-screen flex-col overflow-hidden">
  <!-- top bar -->
  @if (layoutService.isDesktop()) {
    <ng-container *ngTemplateOutlet="desktopGlobalBarTemplate" />
  } @else {
    <ng-container *ngTemplateOutlet="mobileGlobalBarTemplate" />
  }

  <!-- navigation menu -->
  <div class="flex grow overflow-hidden">
    @if (layoutService.isDesktop()) {
      <ng-container *ngTemplateOutlet="desktopNavigationTemplate" />
    } @else {
      <ng-container *ngTemplateOutlet="mobileNavigationTemplate" />
    }

    <main class="h-full grow overflow-y-auto bg-interface-surface-0">
      <!-- router outlet (i.e features) rendered here -->
      <ng-content />
    </main>
  </div>
</div>

<ng-template #desktopGlobalBarTemplate>
  <div class="w-screen bg-interface-primary-color px-3 py-0.5">
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-2">
        @if (navService.isNavigationEnabled()) {
          <div class="relative">
            <!-- popover target highlight -->
            @if (showFirstTimePopover()) {
              <div
                class="absolute inset-0 animate-pulse rounded outline outline-2 outline-border-common-highlighter"
              ></div>
            }
            <p-button
              #firstTimeTarget
              size="small"
              severity="primary"
              (click)="navService.toggleSidebar()"
              [pTooltip]="
                navService.isSidebarOpen()
                  ? 'Collapse Sidebar'
                  : 'Expand Sidebar'
              "
              tooltipPosition="right"
            >
              <ng-template #icon>
                @if (navService.isSidebarOpen()) {
                  <svg-icon
                    class="text-white"
                    src="assets/icons/general/chevron-double-left.svg"
                  />
                } @else {
                  <svg-icon
                    class="text-white"
                    src="assets/icons/general/chevron-double-right.svg"
                  />
                }
              </ng-template>
            </p-button>
          </div>

          <p-popover
            #firstTimePopover
            [dismissable]="false"
            styleClass="[--p-popover-border-color:theme(colors.interface-common.contrast)] [--p-popover-background:theme(colors.interface-common.contrast)] p-popover--with-arrow"
          >
            <ng-template #content>
              <div class="flex flex-col gap-3 p-1 text-white">
                <div class="flex items-center gap-2">
                  <svg-icon
                    src="assets/icons/general/system-feedback-sparkles.svg"
                  />
                  <p>Re-open the side menu here.</p>
                </div>
                <p-button
                  (click)="dismissFirstTimePopover()"
                  label="Got it!"
                  size="small"
                />
              </div>
            </ng-template>
          </p-popover>
        }

        <svg-icon src="assets/icons/brand/ag1-logomark.svg" />

        <h3 class="typography-headings-small text-text-common-inverse-contrast">
          <a routerLink="/" class="hover:!text-text-common-inverse-contrast">
            {{ userService.getCompanyValue()?.name | titlecase }}
          </a>
        </h3>
      </div>
      <div class="flex items-center gap-2">
        <!-- alerts -->
        <app-alerts-menu
          *agHasPermission="PermissionEnum.PagesDemo"
          [alerts]="alerts()"
          (markAllAsRead)="onMarkAllAlertsAsRead()"
          (archiveAllRead)="onArchiveAllReadAlerts()"
          (archiveAll)="onArchiveAllAlerts()"
          (toggleMarkAsRead)="onToggleAlertMarkAsRead($event)"
          (archiveAlert)="onArchiveAlert($event)"
          (alertClick)="onAlertClick($event)"
        />

        <!-- notifications -->
        <app-notifications-v2
          *agHasPermission="PermissionEnum.PagesDemo"
          [notifications]="notifications()"
          (markAllAsRead)="onMarkAllNotificationsAsRead()"
          (archiveAllRead)="onArchiveAllReadNotifications()"
          (archiveAll)="onArchiveAllNotifications()"
          (toggleMarkAsRead)="onToggleNotificationMarkAsRead($event)"
          (archiveAlert)="onArchiveNotification($event)"
          (notificationClick)="onNotificationClick($event)"
        />

        <!-- packhouse connectivity -->
        <app-packhouse-connectivity />

        <!-- support -->
        <!-- TODO UNCOMMENT WHEN WE IMPLEMENT SUPPORT -->
        <!-- <p-button
          size="small"
          pTooltip="Support"
          tooltipPosition="left"
          (click)="navService.navigateToSupport()"
        >
          <ng-template #icon>
            <svg-icon src="assets/icons/general/question-mark-circle.svg" />
          </ng-template>
        </p-button> -->

        <!-- settings -->
        <p-button
          size="small"
          pTooltip="Settings"
          tooltipPosition="left"
          (click)="navService.navigateToSettings()"
        >
          <ng-template #icon>
            <svg-icon src="assets/icons/general/settings.svg" />
          </ng-template>
        </p-button>

        <!-- bentobox -->

        <!-- profile picture -->
        <app-profile-menu
          [alerts]="alerts()"
          [notifications]="notifications()"
          (markAllAsRead)="onMarkAllAlertsAsRead()"
          (archiveAllRead)="onArchiveAllReadAlerts()"
          (archiveAll)="onArchiveAllAlerts()"
          (toggleMarkAsRead)="onToggleAlertMarkAsRead($event)"
          (archiveAlert)="onArchiveAlert($event)"
          (alertClick)="onAlertClick($event)"
          (notificationMarkAllAsRead)="onMarkAllNotificationsAsRead()"
          (notificationArchiveAllRead)="onArchiveAllReadNotifications()"
          (notificationArchiveAll)="onArchiveAllNotifications()"
          (notificationToggleMarkAsRead)="
            onToggleNotificationMarkAsRead($event)
          "
          (notificationArchive)="onArchiveNotification($event)"
          (notificationClick)="onNotificationClick($event)"
        />
      </div>
    </div>
  </div>
</ng-template>

<ng-template #mobileGlobalBarTemplate>
  <div class="w-screen bg-interface-primary-color p-3">
    <div class="flex items-center justify-between gap-2">
      @if (navService.isNavigationEnabled()) {
        <!-- In order to center the logo we stretch the menu size -->
        <div class="w-16">
          <p-button
            size="small"
            severity="primary"
            (click)="navService.toggleMobileMenu()"
            [pTooltip]="
              navService.isMobileMenuOpen() ? 'Close menu' : 'Open menu'
            "
            tooltipPosition="right"
          >
            <ng-template #icon>
              @if (navService.isMobileMenuOpen()) {
                <svg-icon src="assets/icons/general/close.svg" />
              } @else {
                <svg-icon src="assets/icons/general/menu.svg" />
              }
            </ng-template>
          </p-button>
        </div>
      }
      <svg-icon src="assets/icons/brand/ag1-logomark.svg" />
      <app-profile-menu
        [alerts]="alerts()"
        [notifications]="notifications()"
        (markAllAsRead)="onMarkAllAlertsAsRead()"
        (archiveAllRead)="onArchiveAllReadAlerts()"
        (archiveAll)="onArchiveAllAlerts()"
        (toggleMarkAsRead)="onToggleAlertMarkAsRead($event)"
        (archiveAlert)="onArchiveAlert($event)"
        (alertClick)="onAlertClick($event)"
        (notificationMarkAllAsRead)="onMarkAllNotificationsAsRead()"
        (notificationArchiveAllRead)="onArchiveAllReadNotifications()"
        (notificationArchiveAll)="onArchiveAllNotifications()"
        (notificationToggleMarkAsRead)="onToggleNotificationMarkAsRead($event)"
        (notificationArchive)="onArchiveNotification($event)"
        (notificationClick)="onNotificationClick($event)"
      />
    </div>
  </div>
</ng-template>

<ng-template #desktopNavigationTemplate>
  @if (navService.isSidebarOpen()) {
    <aside class="relative z-50 h-full w-64 shrink-0 bg-slate-50">
      @if (navService.isSettingsOpen()) {
        <app-settings />
      } @else {
        <app-navigation />
      }
    </aside>
  }
</ng-template>

<ng-template #mobileNavigationTemplate>
  @if (navService.isMobileMenuOpen()) {
    <div class="absolute inset-0 top-[4rem] z-50 bg-slate-50">
      @if (navService.isSettingsOpen()) {
        <app-settings />
      } @else {
        <app-navigation />
      }
    </div>
  }
</ng-template>
